import React, { FunctionComponent, memo } from "react";
import { RawIntlProvider, IntlShape, createIntl, createIntlCache } from "react-intl";
import { getUserLocale } from "@smartsuite/gridview/lib/locale/intl";

const intlCache: { intl: IntlShape | null } = { intl: null };
const cache = createIntlCache();

interface AsyncGridIntlProviderProps {
  children?: React.ReactNode;
}

/**
 * Provides gridview locale messages. Relies on a parent `<Suspense>` boundary
 * (see GridViewWithLoader): `getIntl()` throws a promise until messages are loaded.
 *
 * Avoid `useMemo` around `getIntl()` — if the initializer throws for Suspense, hook
 * state can interact badly with nested Suspense + `React.lazy` and produce update loops.
 */
const AsyncGridIntlProviderRaw: FunctionComponent<AsyncGridIntlProviderProps> = ({ children }) => {
  const intl = getIntl();
  return <RawIntlProvider value={intl}>{children}</RawIntlProvider>;
};

export const AsyncGridIntlProvider = memo(AsyncGridIntlProviderRaw);

function getIntl(): IntlShape {
  if (intlCache.intl) {
    return intlCache.intl;
  }
  // Suspense: throw the loading promise until gridview locale messages are available.
  // eslint-disable-next-line @typescript-eslint/no-throw-literal -- React Suspense contract
  throw loadIntl();
}

async function loadIntl(): Promise<IntlShape> {
  const locale = getUserLocale();
  const messages = (await import(
    `@smartsuite/gridview/lib/locale/compiled/${locale}.json`
  )) as Record<string, string>;
  const intl = createIntl(
    {
      locale,
      defaultLocale: "en",
      messages,
    },
    cache
  );

  intlCache.intl = intl;
  return intl;
}
